import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import normalizeCss from 'normalize.css';
import lpApi from '../../helpers/lpApi';
import s from './MobilePhotos.css';

// GraphQL op (proxied to besthand-api via /lp-graphql). Appends one uploaded
// photo url to the handoff bucket keyed by sessionId.
const M_ADD_PHOTO = `
  mutation ($sessionId: String!, $url: String!, $name: String) {
    addPhotoToHandoffSession(sessionId: $sessionId, url: $url, name: $name) {
      status
      errorMessage
    }
  }
`;

// Mirror the web /uploadCompanyDocument size limit (the route is the real gate;
// any image type is accepted client-side, see handleFile).
const MAX_BYTES = 15 * 1024 * 1024; // 15 MB

// Guide the client toward enough photos for an accurate, no-visit quote.
// Mirrors TARGET_PHOTO_COUNT in the post-a-project wizard.
const TARGET_PHOTOS = 8;

class MobilePhotos extends React.Component {
  static propTypes = {
    sessionId: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      added: [], // [{ url, name }] — captured this session
      uploading: false,
      error: '',
      expired: false, // session unknown/expired -> graceful dead-end
      finished: false, // user tapped "Done" -> confirmation screen
    };
    this.fileInput = null;
  }

  // Upload one captured file to the existing web upload route, then register it
  // on the handoff session so the PC poll picks it up.
  async handleFile(file) {
    if (!file) return;
    const { sessionId } = this.props;

    // Accept ANY image the phone produces — including iPhone HEIC/HEIF, which the
    // strict jpeg/png/webp list used to reject (a common "my photos won't upload"
    // cause). Some browsers also report an empty type for direct camera captures,
    // so only reject when we positively know it's a non-image file. The server
    // (/uploadCompanyDocument) remains the real validation gate.
    if (file.type && !file.type.startsWith('image/')) {
      this.setState({ error: 'Please choose a photo.' });
      return;
    }
    if (file.size > MAX_BYTES) {
      this.setState({ error: 'That photo is too large (max 15 MB).' });
      return;
    }

    this.setState({ uploading: true, error: '' });
    try {
      const fd = new FormData();
      fd.append('document', file);
      const resp = await fetch('/uploadCompanyDocument', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
      });
      const json = await resp.json();
      if (!json || json.status !== 200 || !json.documentUrl) {
        this.setState({ uploading: false, error: (json && json.errorMessage) || 'Upload failed. Please try again.' });
        return;
      }

      const url = json.documentUrl;
      const res = await lpApi(M_ADD_PHOTO, { sessionId, url, name: file.name || null });
      const env = res && res.data && res.data.addPhotoToHandoffSession;
      if (res && res.errors) {
        this.setState({ uploading: false, error: 'Could not reach your computer session. Please try again.' });
        return;
      }
      if (!env || env.status === 404 || env.status === 410) {
        // Session is gone — stop the flow cleanly.
        this.setState({ uploading: false, expired: true });
        return;
      }
      if (env.status !== 200) {
        this.setState({ uploading: false, error: env.errorMessage || 'Something went wrong. Please try again.' });
        return;
      }

      this.setState((st) => ({
        uploading: false,
        error: '',
        added: st.added.concat({ url, name: file.name || 'Photo' }),
      }));
    } catch (e) {
      this.setState({ uploading: false, error: `Upload failed: ${e.message}` });
    }
  }

  openCamera() {
    if (this.fileInput) this.fileInput.click();
  }

  render() {
    const { added, uploading, error, expired, finished } = this.state;
    const count = added.length;
    const met = count >= TARGET_PHOTOS;
    const pct = Math.min(100, Math.round((count / TARGET_PHOTOS) * 100));

    if (finished) {
      return (
        <div className={s.page}>
          <div className={s.card}>
            <div className={s.logo}>BestHand</div>
            <div className={s.finishedIcon} aria-hidden>✅</div>
            <h1 className={s.title}>All set — {count} photo{count === 1 ? '' : 's'} added</h1>
            <p className={s.sub}>
              Your photos are now on your computer. Head back there to finish and publish
              your project. You can close this page.
            </p>
            <button
              type="button"
              className={s.doneBtn}
              onClick={() => this.setState({ finished: false })}
            >
              Add more photos
            </button>
          </div>
        </div>
      );
    }

    if (expired) {
      return (
        <div className={s.page}>
          <div className={s.card}>
            <div className={s.logo}>BestHand</div>
            <div className={s.expiredIcon} aria-hidden>⌛</div>
            <h1 className={s.title}>This code has expired</h1>
            <p className={s.sub}>
              For your security, add-photo codes only last a short while. Head back to your
              computer and tap “Add photos from your phone” again to get a fresh code.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={s.page}>
        <div className={s.card}>
          <div className={s.logo}>BestHand</div>
          <h1 className={s.title}>Add photos for your project</h1>
          <p className={s.sub}>
            Take photos here on your phone — they’ll show up on your computer automatically.
          </p>

          {/* Guide: aim for at least 8 photos so companies can quote without a visit. */}
          <div className={s.guide}>
            <div className={s.guideRow}>
              <span className={s.guideLabel}>Take at least {TARGET_PHOTOS} photos</span>
              <span className={met ? `${s.guideCount} ${s.met}` : s.guideCount}>
                {count} of {TARGET_PHOTOS}
              </span>
            </div>
            <div className={s.progressTrack}>
              <div
                className={met ? `${s.progressFill} ${s.met}` : s.progressFill}
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className={s.guideHint}>
              {met
                ? 'Great — that’s enough for an accurate quote. Add more if you like.'
                : 'Get clear shots from different angles — every wall, the floor, fixtures, and any problem areas. More photos = more accurate quotes, with no site visit.'}
            </p>
          </div>

          <button
            type="button"
            className={s.cameraBtn}
            onClick={() => this.openCamera()}
            disabled={uploading}
          >
            <span className={s.cameraIcon} aria-hidden>📷</span>
            {uploading ? 'Uploading…' : (count ? 'Add another photo' : 'Take a photo')}
          </button>

          {/* capture="environment" makes phones open the rear camera directly */}
          <input
            ref={(el) => { this.fileInput = el; }}
            type="file"
            accept="image/*"
            capture="environment"
            className={s.hiddenInput}
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              this.handleFile(f);
              e.target.value = '';
            }}
          />

          {error ? <div className={s.error}>{error}</div> : null}

          {added.length > 0 ? (
            <div className={s.addedWrap}>
              <div className={s.addedHead}>
                <span className={s.check} aria-hidden>✓</span>
                Added — they’re now on your computer
              </div>
              <div className={s.thumbGrid}>
                {added.map((p, i) => (
                  <div className={s.thumb} key={`${p.url}-${i}`}>
                    <img src={p.url} alt={p.name || 'Photo'} />
                  </div>
                ))}
              </div>
              <p className={s.doneNote}>
                Keep adding photos, or tap Done — then finish your project back on your computer.
              </p>
              <button
                type="button"
                className={met ? `${s.doneBtn} ${s.ready}` : s.doneBtn}
                onClick={() => this.setState({ finished: true })}
              >
                {met ? 'Done — back to my computer' : `Done (${count} of ${TARGET_PHOTOS})`}
              </button>
            </div>
          ) : (
            <p className={s.emptyNote}>
              Tip: get a few clear shots from different angles for the best quote.
            </p>
          )}
        </div>
      </div>
    );
  }
}

export default withStyles(normalizeCss, s)(MobilePhotos);
